import { CalendarWeek, FileAlt, Wifi } from '@/client/components/icons/ContinuIcons';
import { Circle, Flex, HStack, Icon, Stack, Text } from '@chakra-ui/react';

import AssignmentBuilderPreviewStatus from '../data-display/AssignmentBuilderPreviewStatus';
import type { AssignmentFormData } from '../../types/assignmentTypes';
import { format } from 'date-fns';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AssignmentBuilderHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const isInstanceCreation = location.pathname.includes('instance');
  const { watch } = useFormContext<AssignmentFormData>();
  const { previewStatusMap, stepIndex } = useAdminAssignmentsStore();

  const status = previewStatusMap.generalDetails;

  const typeMap: { [key: string]: string } = {
    pointInTime: t('admin.assignments.details.direct'),
    automated: t('admin.assignments.details.automated'),
  };

  const { settings, simpleType, createdAt } = watch();

  const { name } = settings;

  const statusStyles: { [key: string]: { borderColor: string; boxShadow: string } } = {
    completed: {
      borderColor: 'transparent',
      boxShadow: `none`,
    },
    inProgress: {
      borderColor: 'warmNeutral.300',
      boxShadow: `0px 0px 4px 8px var(--chakra-colors-warmNeutral-100)`,
    },
    notStarted: {
      borderColor: 'transparent',
      boxShadow: 'none',
    },
    none: {
      borderColor: 'transparent',
      boxShadow: 'none',
    },
  };

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      position="sticky"
      backgroundColor="white"
      top={0}
      paddingY={5}
      paddingX={6}
      border="1px solid"
      borderRadius="16px"
      marginLeft={-20}
      marginRight={-20}
      zIndex={998}
      sx={
        isInstanceCreation
          ? statusStyles.none
          : !name || !simpleType || stepIndex === 0
          ? statusStyles[status]
          : statusStyles.none
      }
    >
      <Stack>
        <Text fontSize="18px" fontWeight={600}>
          {name || t('admin.assignments.newAssignment')}
        </Text>

        <HStack spacing={2}>
          {simpleType && (
            <HStack spacing={0.5}>
              <Circle backgroundColor="neutral.50" size="24px">
                <Icon
                  as={simpleType === 'pointInTime' ? FileAlt : Wifi}
                  boxSize={3}
                  color="neutral.700"
                />
              </Circle>

              <Text fontSize="12px" fontWeight={600} color="neutral.700">
                {typeMap[simpleType]}
              </Text>
            </HStack>
          )}

          {createdAt && (
            <HStack>
              <Circle backgroundColor="neutral.50" size="24px">
                <Icon as={CalendarWeek} boxSize={3} color="neutral.700" />
              </Circle>

              <Text fontSize="12px" fontWeight={600} color="neutral.700">
                {`${t('admin.assignments.details.created')} ${format(
                  new Date(createdAt),
                  'MMM d, yyyy h:mm a',
                )}`}
              </Text>
            </HStack>
          )}
        </HStack>
      </Stack>

      {(!name || !simpleType || stepIndex === 0) && !isInstanceCreation && (
        <AssignmentBuilderPreviewStatus form="generalDetails" />
      )}
    </Flex>
  );
}
